import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getSelectionLabel } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AD_UNLOCK_SECONDS = 5;
const LANG_STORAGE_KEY = 'daily_tips_lang';

const TRANSLATIONS = {
  el: {
    pageTitle: 'Προγνωστικά Ποδοσφαίρου Ημέρας — Δωρεάν',
    eyebrow: 'Προγνωστικά Ημέρας',
    headline: 'Τα Σημερινά Προγνωστικά',
    subhead:
      'Οι επιλογές της ημέρας — ένα μονό, ένα διπλό και ένα τριπλό. Χωρίς λογαριασμό, απλά ξεκλείδωσε για να δεις.',
    unlockTitle: 'Δες ένα σύντομο βίντεο για να εμφανιστούν τα προγνωστικά',
    unlockBody: 'Δωρεάν προγνωστικά, κάθε μέρα — υποστηρίζονται από ένα σύντομο βίντεο.',
    watchAd: 'Δες διαφήμιση για ξεκλείδωμα',
    loadingAd: 'Φόρτωση…',
    alreadyAccount: 'Έχεις ήδη λογαριασμό;',
    signInAdFree: 'Σύνδεση για προγνωστικά χωρίς διαφήμιση',
    loading: 'Φορτώνονται τα σημερινά προγνωστικά…',
    errorMsg: 'Δεν ήταν δυνατή η φόρτωση των προγνωστικών. Δοκίμασε ξανά σε λίγο.',
    preparedTitle: 'Τα σημερινά προγνωστικά ετοιμάζονται',
    preparedBody: 'Έλεγξε ξανά σε λίγα λεπτά — νέες επιλογές ανεβαίνουν κάθε πρωί.',
    singles: 'Μονά',
    single: 'Μονό',
    double: 'Διπλό',
    treble: 'Τριπλό',
    noSingle: 'Δεν υπάρχει μονό προγνωστικό σήμερα.',
    noDouble: 'Δεν υπάρχει διπλό προγνωστικό σήμερα.',
    noTreble: 'Δεν υπάρχει τριπλό προγνωστικό σήμερα.',
    pick: 'Επιλογή',
    picksCount2: '2 επιλογές',
    picksCount3: '3 επιλογές',
    ctaTitle: 'Θες τα προγνωστικά κάθε μέρα — χωρίς διαφήμιση;',
    ctaBody:
      'Δημιούργησε δωρεάν λογαριασμό και πάρε καθημερινά προγνωστικά απευθείας στον πίνακά σου. Δεν χρειάζεται κάρτα.',
    createFreeAccount: 'Δημιουργία δωρεάν λογαριασμού',
    signIn: 'Σύνδεση',
    disclaimer:
      'Μόνο για ενημερωτικούς και ψυχαγωγικούς σκοπούς. 18+. Παίζε υπεύθυνα.',
    langToggle: 'EN',
  },
  en: {
    pageTitle: "Daily Football Tips — Free Picks of the Day",
    eyebrow: 'Daily Picks',
    headline: "Today's Football Tips",
    subhead:
      "Our picks of the day — one single, one double and one treble. No account needed, just unlock to view.",
    unlockTitle: "Watch a short ad to reveal today's picks",
    unlockBody: 'Free picks, every day — supported by a quick rewarded video.',
    watchAd: 'Watch ad to unlock picks',
    loadingAd: 'Loading…',
    alreadyAccount: 'Already have an account?',
    signInAdFree: 'Sign in for ad-free daily tips',
    loading: "Loading today's picks…",
    errorMsg: "Could not load today's tips. Please try again shortly.",
    preparedTitle: "Today's picks are being prepared",
    preparedBody: 'Check back in a few minutes — new tips drop every morning.',
    singles: 'Singles',
    single: 'Single',
    double: 'Double',
    treble: 'Treble',
    noSingle: 'No single tip today.',
    noDouble: 'No double tip today.',
    noTreble: 'No treble tip today.',
    pick: 'Pick',
    picksCount2: '2 picks',
    picksCount3: '3 picks',
    ctaTitle: 'Want these tips every day — without the ad?',
    ctaBody:
      'Create a free account and get daily football picks delivered straight to your dashboard. No card required.',
    createFreeAccount: 'Create free account',
    signIn: 'Sign in',
    disclaimer:
      'For entertainment and informational purposes only. 18+. Please gamble responsibly.',
    langToggle: 'ΕΛ',
  },
};

const PredictionBlock = ({ title, badge, prediction, empty, t }) => {
  if (!prediction || !prediction.matches || prediction.matches.length === 0) {
    return (
      <Card
        data-testid={`daily-tips-${title.toLowerCase()}-empty`}
        className="bg-white/5 border border-white/10 p-5 rounded-2xl"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-white font-semibold text-lg">{title}</h3>
          {badge}
        </div>
        <p className="text-gray-400 text-sm">{empty}</p>
      </Card>
    );
  }

  return (
    <Card
      data-testid={`daily-tips-${title.toLowerCase()}`}
      className="bg-white/5 border border-white/10 p-5 rounded-2xl"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-lg">{title}</h3>
        <div className="flex items-center gap-2">
          {badge}
          {prediction.total_odds && (
            <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-1 rounded-full font-semibold">
              @ {Number(prediction.total_odds).toFixed(2)}
            </span>
          )}
        </div>
      </div>
      <div className="space-y-3">
        {prediction.matches.map((m, i) => (
          <div
            key={i}
            className="bg-slate-900/60 border border-white/5 rounded-xl p-3 flex flex-col gap-1"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-white font-medium text-sm truncate">
                {m.home_team} vs {m.away_team}
              </span>
              {m.odds && (
                <span className="text-xs text-emerald-300 font-semibold whitespace-nowrap">
                  {Number(m.odds).toFixed(2)}
                </span>
              )}
            </div>
            {m.league && (
              <span className="text-xs text-gray-500 truncate">{m.league}</span>
            )}
            <span className="text-sm text-blue-300 font-medium">
              {t.pick}: {getSelectionLabel(m.selection, m.home_team, m.away_team)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};

const DailyTips = () => {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem(LANG_STORAGE_KEY) || 'el';
    } catch {
      return 'el';
    }
  });
  const t = TRANSLATIONS[lang] || TRANSLATIONS.el;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [adCountdown, setAdCountdown] = useState(0);
  const [adPlaying, setAdPlaying] = useState(false);

  // Detect the PAOKFC.NET host app (UA sniff — their Android/iOS WebView
  // appends "PAOKFCApp" to the user-agent).
  const isPaokApp =
    typeof navigator !== 'undefined' && /PAOKFCApp/i.test(navigator.userAgent);

  const timerRef = React.useRef(null);
  const runCountdown = React.useCallback(() => {
    setAdPlaying(true);
    setAdCountdown(AD_UNLOCK_SECONDS);
    const start = Date.now();
    const tick = () => {
      const left = Math.max(
        0,
        AD_UNLOCK_SECONDS - Math.floor((Date.now() - start) / 1000)
      );
      setAdCountdown(left);
      if (left <= 0) {
        setUnlocked(true);
        setAdPlaying(false);
      } else {
        timerRef.current = setTimeout(tick, 250);
      }
    };
    timerRef.current = setTimeout(tick, 250);
  }, []);

  // Listen for the PAOK app's reply (window message `type: "paokReward"`).
  useEffect(() => {
    if (!isPaokApp) return undefined;
    const handler = (event) => {
      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }
      if (!payload || payload.type !== 'paokReward') return;
      const status = payload.status;
      if (status === 'earned') {
        if (timerRef.current) clearTimeout(timerRef.current);
        setAdPlaying(false);
        setUnlocked(true);
      } else if (status === 'dismissed') {
        if (timerRef.current) clearTimeout(timerRef.current);
        setAdPlaying(false);
        setUnlocked(false);
      } else if (status === 'unavailable') {
        if (timerRef.current) clearTimeout(timerRef.current);
        setAdPlaying(false);
        runCountdown();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isPaokApp, runCountdown]);

  useEffect(() => {
    document.title = t.pageTitle;
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* storage disabled */
    }
  }, [lang, t.pageTitle]);

  useEffect(() => {
    axios
      .get(`${API}/public/daily-tips`)
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const toggleLang = () => setLang((l) => (l === 'el' ? 'en' : 'el'));

  const startAd = () => {
    if (adPlaying || unlocked) return;

    // Inside the PAOKFC.NET app: ask the host to show its rewarded video
    // and wait for a `paokReward` message. We do NOT run a timer here so
    // that no Google ad SDK is pulled onto the page.
    if (isPaokApp) {
      setAdPlaying(true);
      setAdCountdown(0);
      const payload = JSON.stringify({ type: 'requestReward' });
      try {
        if (window.ReactNativeWebView?.postMessage) {
          window.ReactNativeWebView.postMessage(payload);
        } else if (window.PAOKFCApp?.postMessage) {
          window.PAOKFCApp.postMessage(payload);
        } else if (
          window.webkit?.messageHandlers?.PAOKFCApp?.postMessage
        ) {
          window.webkit.messageHandlers.PAOKFCApp.postMessage(payload);
        } else {
          // Last-resort bridge used by some WebView wrappers.
          window.postMessage(payload, '*');
        }
      } catch {
        // If we can't reach the host for any reason, fall back to the timer.
        setAdPlaying(false);
        runCountdown();
      }
      return;
    }

    // Normal browser: unchanged 5-second placeholder.
    runCountdown();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Language toggle (top-right) */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 flex justify-end">
        <button
          onClick={toggleLang}
          data-testid="daily-tips-lang-toggle"
          className="text-xs font-semibold bg-white/5 hover:bg-white/10 border border-white/15 text-white px-3 py-1.5 rounded-full transition-colors"
          aria-label="Toggle language"
        >
          {t.langToggle}
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-10 pt-4 sm:pt-6">
        {/* Logo */}
        <div className="flex items-center justify-center mb-6">
          <Link to="/" data-testid="daily-tips-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_gameday-insights/artifacts/xo9zb1rd_bettingtracker-removebg-preview.png"
              alt="BettingTracker.Pro"
              className="h-12 sm:h-14 w-auto"
            />
          </Link>
        </div>

        <div className="text-center mb-8">
          <span className="inline-block text-[11px] uppercase tracking-widest text-emerald-300 font-semibold mb-2">
            {t.eyebrow} · {data?.date || new Date().toISOString().slice(0, 10)}
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight">
            {t.headline}
          </h1>
          <p className="text-gray-300 text-sm sm:text-base mt-3 max-w-xl mx-auto">
            {t.subhead}
          </p>
        </div>

        {/* Unlock gate */}
        {!unlocked && !loading && !error && (
          <Card
            data-testid="daily-tips-unlock-card"
            className="bg-white/5 border border-white/10 p-6 sm:p-8 rounded-2xl text-center mb-8"
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-2">
              {t.unlockTitle}
            </h2>
            <p className="text-gray-400 text-sm mb-6">{t.unlockBody}</p>
            <Button
              data-testid="daily-tips-watch-ad-btn"
              onClick={startAd}
              disabled={adPlaying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-3 text-base font-semibold"
            >
              {adPlaying
                ? isPaokApp
                  ? t.loadingAd
                  : `${t.loadingAd} ${adCountdown}s`
                : t.watchAd}
            </Button>
            <p className="text-[11px] text-gray-500 mt-4">
              {t.alreadyAccount}{' '}
              <Link to="/login" className="text-blue-300 hover:underline">
                {t.signInAdFree}
              </Link>
            </p>
          </Card>
        )}

        {loading && (
          <div
            data-testid="daily-tips-loading"
            className="text-center text-gray-400 py-10"
          >
            {t.loading}
          </div>
        )}

        {error && (
          <Card className="bg-rose-500/10 border border-rose-500/30 p-5 rounded-2xl text-center">
            <p className="text-rose-200">{t.errorMsg}</p>
          </Card>
        )}

        {/* Picks (hidden until unlocked) */}
        {unlocked && !loading && !error && data && (
          <div className="space-y-5" data-testid="daily-tips-picks">
            {!data.has_predictions ? (
              <Card className="bg-amber-500/10 border border-amber-500/30 p-6 rounded-2xl text-center">
                <h3 className="text-amber-200 font-semibold mb-1">
                  {t.preparedTitle}
                </h3>
                <p className="text-gray-300 text-sm">{t.preparedBody}</p>
              </Card>
            ) : (
              <>
                <div>
                  <h2 className="text-sm uppercase tracking-widest text-gray-400 mb-3">
                    {t.singles}
                  </h2>
                  {data.singles && data.singles.length > 0 ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      {data.singles.map((s, i) => (
                        <PredictionBlock
                          key={s.id || i}
                          title={`${t.single} ${i + 1}`}
                          badge={null}
                          prediction={s}
                          empty={t.noSingle}
                          t={t}
                        />
                      ))}
                    </div>
                  ) : (
                    <Card className="bg-white/5 border border-white/10 p-5 rounded-2xl">
                      <p className="text-gray-400 text-sm">{t.noSingle}</p>
                    </Card>
                  )}
                </div>

                <PredictionBlock
                  title={t.double}
                  badge={
                    <span className="text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-1 rounded-full font-semibold uppercase tracking-wider">
                      {t.picksCount2}
                    </span>
                  }
                  prediction={data.double}
                  empty={t.noDouble}
                  t={t}
                />

                <PredictionBlock
                  title={t.treble}
                  badge={
                    <span className="text-[10px] bg-purple-500/20 text-purple-300 border border-purple-500/30 px-2 py-1 rounded-full font-semibold uppercase tracking-wider">
                      {t.picksCount3}
                    </span>
                  }
                  prediction={data.treble}
                  empty={t.noTreble}
                  t={t}
                />
              </>
            )}

            {/* Signup CTA */}
            <Card
              data-testid="daily-tips-signup-card"
              className="bg-gradient-to-br from-emerald-500/20 to-blue-700/20 border border-emerald-500/30 p-6 sm:p-8 rounded-2xl text-center"
            >
              <h3 className="text-xl sm:text-2xl font-bold mb-2">
                {t.ctaTitle}
              </h3>
              <p className="text-gray-200 text-sm sm:text-base mb-5 max-w-xl mx-auto">
                {t.ctaBody}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/signup">
                  <Button
                    data-testid="daily-tips-signup-btn"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-3 text-base font-semibold w-full sm:w-auto"
                  >
                    {t.createFreeAccount}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    data-testid="daily-tips-signin-btn"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-3 text-base font-semibold w-full sm:w-auto bg-transparent"
                  >
                    {t.signIn}
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        )}

        {/* Footer disclaimer */}
        <p className="text-[11px] text-gray-500 text-center mt-10 max-w-xl mx-auto">
          {t.disclaimer}
        </p>
      </div>
    </div>
  );
};

export default DailyTips;
